import {
  useProfileService,
  useRegulatoryWatchService,
} from '@/services/regulatoryWatch';
import { type FC, useMemo, type ReactNode } from 'react';
import { FeedMissingSubscriptionHintProvider } from '../components/FeedMissingSubscriptionHint';
import { ReviewCardList } from '../components/ReviewCardList';
import { UpdatesTable } from '../components/UpdatesTable';
import { useAssessmentProgress } from '../hooks/useAssessmentProgress';

/** Shared assessment state consumed by all regulatory-watch feed routes. */
const useFeedAssessment = () => {
  const { profile } = useProfileService({ refetchInterval: 3000 });
  return useAssessmentProgress({
    batchSize: profile?.impactAssessmentBatchSize ?? 0,
    batchCompleted: profile?.impactAssessmentBatchCompleted ?? 0,
  });
};

const FeedShell: FC<{ children: ReactNode }> = ({ children }) => (
  <FeedMissingSubscriptionHintProvider>
    <div className="flex flex-col min-h-0 flex-1 w-full">{children}</div>
  </FeedMissingSubscriptionHintProvider>
);

export const NewsfeedPage: FC = () => {
  const { showProgress: isAssessing, progress: assessmentProgress } =
    useFeedAssessment();

  const { updates: allUpdates } = useRegulatoryWatchService(
    { subscribedOnly: 'true' },
    { refetchInterval: 3000 }
  );

  const { totalCount, outOfScopeCount } = useMemo(() => {
    let outOfScope = 0;
    for (const u of allUpdates) {
      if ((u.context?.status ?? 'to_review') === 'out_of_scope') outOfScope++;
    }
    return { totalCount: allUpdates.length, outOfScopeCount: outOfScope };
  }, [allUpdates]);

  return (
    <FeedShell>
      <ReviewCardList
        isBatchAssessing={isAssessing}
        assessmentProgress={assessmentProgress}
        bannerTotalUpdates={totalCount}
        bannerOutOfScopeCount={outOfScopeCount}
      />
    </FeedShell>
  );
};

export const AuditTablePage: FC = () => {
  const { showProgress: isAssessing, progress: assessmentProgress } =
    useFeedAssessment();
  return (
    <FeedShell>
      <UpdatesTable
        subscribedOnly
        isBatchAssessing={isAssessing}
        assessmentProgress={assessmentProgress}
        variant="audit"
        sessionPageId="audit-table"
      />
    </FeedShell>
  );
};

export const AllUpdatesPage: FC = () => {
  const { showProgress: isAssessing, progress: assessmentProgress } =
    useFeedAssessment();
  return (
    <FeedShell>
      <UpdatesTable
        subscribedOnly
        isBatchAssessing={isAssessing}
        assessmentProgress={assessmentProgress}
        variant="allUpdates"
        sessionPageId="all-updates"
      />
    </FeedShell>
  );
};
